import { Component, OnInit } from '@angular/core';
import { FacilitiesService, Facility } from '../../core/facilities/facilities.service';
import { AbstractBaseComponent } from '../abstract-base.component';
import { SessionStateService } from '../../core/user/session-state.service';
import { ModalDirective } from "ngx-bootstrap/modal";
import { Router } from "@angular/router";
import { takeUntil } from 'rxjs/operators';
import { UserService } from '../../core';


const STATE_KEY = "FacilitySelector";
interface FacilitySelectorState {
    ids: string[];
}

/**
 * U/I component to show a dropdown menu of available Facilities, allow users to make selections,
 * and notify other components of the new selections.
 */
@Component({
    selector: 'my-global-facility-selector',
    templateUrl: 'global-facility-selector.component.html',
    styleUrls: ['global-facility-selector.component.scss']
})
export class GlobalFacilitySelectorComponent extends AbstractBaseComponent implements OnInit {

    /** All available facilities (includes selected field) */
    available: Facility[] = [];
    /** All available facilities based on IP restrictions */
    allowedFacilities: Facility[] = [];
    /** Copy of only the selected=true available facilities */
    selected: Facility[] = [];

    /** Are all available facilities selected? */
    isAllSelected = false;

    /** Is only one facility selected? */
    isOneSelected = false;

    /** Has a change been made since last notifying subscribers? */
    dirty = false;

    singleOnlyPage = false;

    /** 237770 - Add search functionality to facility selector */
    searchTerm: string = "";
    filtered: Facility[] = [];
    get isSearching(): boolean { return this.searchTerm.length > 0; }
    get showSearch(): boolean {
        return (this.available && this.available.length > 1);
    }
    get hasResults(): boolean { return this.filtered.length > 0; }

    /** Construct */
    constructor(private facilitiesSvc: FacilitiesService, private userSvc: UserService, private sessionSvc: SessionStateService, private router: Router,) {
        super();
    }

    /**
     * Start subscriptions.
     */
    ngOnInit() {
        this.facilitiesSvc.allFacilities$.pipe(takeUntil(this.destroy$)).subscribe(
            (facilities: Facility[]) => this.onNewFacilityList(facilities));

        this.facilitiesSvc.singleSelectedFacilityPage$.pipe(takeUntil(this.destroy$)).subscribe(
            (value: boolean) => this.onSelectSingleFacility(value));

        this.facilitiesSvc.selectedFacilities$.pipe(takeUntil(this.destroy$)).subscribe(
            (facilities: Facility[]) => this.onSelectFacilities(facilities));
    }

    onSelectFacilities(facilities: Facility[]) {
        if(this.available) {

            this.available.forEach(availableFacility => availableFacility.selected = false);
            facilities.forEach(facility => {
                this.available.filter(avail => avail.id === facility.id)
                    .forEach(f => {
                        f.selected = true;
                        this.facilitiesSvc.setParentsAndChildren(this.available, f);
                    });
            });
            this.onSelectionChange(null);

            let state: FacilitySelectorState = {ids: this.selected.map(f => f.id)};
            this.sessionSvc.save(STATE_KEY, state);
            this.dirty = false;
        }
    }

    onSelectSingleFacility(value: boolean){
        this.singleOnlyPage = value;
    }

    /**
     * User clicked button on U/I
     */
    onSelectAll() {
        const facilityList = this.isSearching ? this.filtered : this.allowedFacilities;
        facilityList.forEach(f => {
            if (!f.selected) {
                f.selected = true;
                this.dirty = true;
            }
        });
        this.onSelectionChange(null);
    }

    /**
     * User has changed a selection.
     */
    onSelectionChange(facility: Facility) {
        this.dirty = true;

        if(this.singleOnlyPage && facility){
            setTimeout(() => {
                // Toggle all facilities so that the recently clicked becomes the only selection.
                this.available.forEach(f => f.selected = false);
                this.available.find(f => f.id === facility.id).selected=true;
                this.selected = this.available.filter(f => f.selected);
                this.isAllSelected = false;
                this.isOneSelected = (this.selected.length === 1);
            });
        }else {
            // Set now so that header updates
            this.selected = this.available.filter(f => f.selected && f.isFacility);

            // If were all selected, and now all but one is...
            let availableLength = this.available.filter(f => f.isFacility).length;
            if ((this.isAllSelected && this.selected.length === availableLength - 1 && availableLength > 2) 
                || (this.isAllSelected && this.selected.length === availableLength && availableLength > 2 && facility && !facility.isFacility)){
                // Delay, because we're in a ngModelChanged event handler right now
                // and we're about to change the very thing we're being notified about.
                setTimeout(() => {
                    // Toggle all facilities so that the recently clicked becomes the only selection.
                    this.available.forEach(f => f.selected = !f.selected);
                    this.setFacilityCheckboxes(facility);
                });
            }
            else {
                setTimeout(() => {
                    this.setFacilityCheckboxes(facility);
                });
            }
        }
    }

    setFacilityCheckboxes(facility: Facility) {
        let availableLength = this.available.filter(f => f.isFacility).length;

        if (facility && facility.companyName) {
            this.facilitiesSvc.setParentsAndChildren(this.available, facility);
        }

        this.selected = this.available.filter(f => f.selected && f.isFacility);
        this.isAllSelected = (this.selected.length === availableLength);
        this.isOneSelected = (this.selected.length === 1);
    }
 
    /**
     * The dropdown selector has been opened or closed.
     *
     * @param isOpen true when opening, false when closing.
     */
    onDropdownToggle(isOpen: boolean) {
        if (!isOpen && this.dirty) {
            // Save state in session so that we remember upon page refresh
            let state: FacilitySelectorState = { ids: this.selected.map(f => f.id )};
            this.sessionSvc.save(STATE_KEY, state);

            // Notify listeners
            this.dirty = false;
            this.facilitiesSvc.selectedFacilities$.next(this.selected);
        }
    }

    onSearchTermChanged() {
        if (!this.isSearching) {
            this.filtered = [];
            return;
        }
        const lookingFor = this.searchTerm.toLowerCase();
        const results = this.allowedFacilities.filter(f => {
                return f.name.toLowerCase().includes(lookingFor) ||
                f.regionName.toLowerCase().includes(lookingFor) ||
                f.divisionName.toLowerCase().includes(lookingFor) ||
                (this.userSvc.isImpersonating && f.companyName.toLowerCase().includes(lookingFor));
            }
        );
        this.filtered = results;
    }

    clearSearch(): void {
        this.searchTerm = "";
    }

    /**
     * A new list of available Facilities has been received.
     * Generally happens on login or logout.
     *
     * @param facilities
     */
    private onNewFacilityList(facilities: Facility[]): void {
        if (facilities && facilities.length) {
            this.available = facilities;
            this.allowedFacilities = this.facilitiesSvc.allowedFacilities;
            
            // Prevent user login if no facilities are selectable
            if (this.allowedFacilities && this.allowedFacilities.length == 0){
                this.router.navigate(['/access-denied']);
            }

            // Update selected flags according to saved state
            let state = this.sessionSvc.load(STATE_KEY) as FacilitySelectorState;

            if (state && state.ids) {
                // Just select what was before selected
                let previousIds = new Set(state.ids);
                this.available.forEach(f => f.selected = previousIds.has(f.id));
                this.selected = this.available.filter(f => f.selected);

                // If nothing selected... well now that's odd. Do the default behavior.
                if (this.selected.length === 0 && !this.isSearching) {
                    this.doInitialDefaultSelection();
                }
            }
            else {
                if (!this.isSearching)
                    // No saved state, so do the default behavior.
                    this.doInitialDefaultSelection();
            }

            this.isOneSelected = (this.selected.length === 1);
            this.isAllSelected = (this.available.filter(f => f.isFacility).length === this.selected.length);
        }
        else {
            // No facilities - must have logged out.
            this.available = [];
            this.selected = [];
        }

        // Notify listeners
        this.dirty = false;
        this.facilitiesSvc.selectedFacilities$.next(this.selected);
    }

    /**
     * Helper to do whatever the current requirements are for the default selection.
     * Only to be used within the context of onNewFacilityList().
     */
    private doInitialDefaultSelection() {
        let currentUrl = this.router.url;
        if (this.available.length === 0)
            return;

        // Unselect all facilities
        this.available.forEach((f => f.selected = false));

        /*
         * If we are on the following routes, select all. We need to do it here instead of in the component
         * because otherwise there is a race condition during login between this initial selection and selecting all facilities
         * in the component.
        */

        if (currentUrl == "/alerts/landing" || currentUrl == "/resources/pharmacy-communications") {
            this.selected.forEach(x => x.selected = true);
            this.selected = this.available.filter(x => x.isFacility);
        }
        // Else select the first in the list of selectable facilities
        else {
            if (this.allowedFacilities.length > 0){

                //Select the first facility in the list(and set its parents if need be)
                let first = this.available.find(x => x.isFacility);
                first.selected = true;
                this.selected.push(first);
            }
        }
    }

    isFacilityDisabled(facility: Facility){
        return facility && !facility.isIpAllowedToAccessFacility;
    }

    isCustomOrgUnit(facility: Facility){
        return facility && !facility.isFacility;
    }

    getFacilityTooltipText(facility: Facility){
        return facility && facility.isIpAllowedToAccessFacility ? "" : "Access Denied: IP Restriction.";
    }

    /**
     * Close the designated modal and clean up.
     * @param modal
     */
    closeModal(modal: ModalDirective) {
        modal.hide();
    }

    /**
     * Determines what kind of "facility" the provided facility is.  I use the term facility
     * loosely here, as companies, regions, divisions, and facilities are all considered
     * facilities by the logic of this component
     */
     public getFacilityTypeDataAttribute(fac: Facility): string {
        return fac.unitType.toLowerCase();
    }
}

